import * as React from 'react'

import { ChevronRight } from 'lucide-react'

import OptionsCheckItem from '~/client/src/shared/components/OptionsList/pieces/OptionsCheckItem'
import OptionsEmpty from '~/client/src/shared/components/OptionsList/pieces/OptionsEmpty'
import OptionsItemList from '~/client/src/shared/components/OptionsList/pieces/OptionsItemList'
import OptionsListHeader from '~/client/src/shared/components/OptionsList/pieces/OptionsListHeader'
import OptionsSeparator from '~/client/src/shared/components/OptionsList/pieces/OptionsSeparator'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { useDrillDownFilterContext } from '../context'
import { ResetButton } from '../DrillDownFilterPieces'
import { filterDrillDownCategoryItems } from '../filterCategories'

export default function TreeItemList({
  resetSize,
}: {
  resetSize?: 'sm' | 'default' | 'lg'
}) {
  const { state, actions, meta } = useDrillDownFilterContext()
  const category = meta.selectedCategory

  const childrenByParent = React.useMemo(() => {
    const map = new Map<string | null, unknown[]>()
    if (!category || category.kind !== 'tree') return map

    const itemIds = new Set(
      category.items.map(item => category.getItemId(item)),
    )
    category.items.forEach(item => {
      const parentId = category.getItemParentId(item)
      const key = parentId && itemIds.has(parentId) ? parentId : null
      const list = map.get(key) ?? []
      list.push(item)
      map.set(key, list)
    })
    return map
  }, [category])

  if (!category || category.kind !== 'tree') return null

  const isSearching = !!state.searchTerm.trim()
  const rows = isSearching
    ? filterDrillDownCategoryItems(category, state.searchTerm)
    : (childrenByParent.get(meta.currentPathNodeId) ?? [])

  if (rows.length === 0) {
    return (
      <OptionsItemList>
        <OptionsEmpty />
      </OptionsItemList>
    )
  }

  const selectedCount = rows.filter(item =>
    state.pendingSelection.has(category.getItemId(item)),
  ).length
  const isAllSelected = selectedCount === rows.length
  const isPartiallySelected = selectedCount > 0 && selectedCount < rows.length

  const toggleAll = () =>
    actions.onToggleItems(
      rows.map(item => category.getItemId(item)),
      !isAllSelected,
    )

  return (
    <>
      <OptionsListHeader
        checked={isAllSelected}
        indeterminate={isPartiallySelected}
        onToggle={toggleAll}
        label={`${rows.length} ${category.label} / ${selectedCount} ${Localization.translator.selected}`}
        action={
          state.pendingSelection.size > 0 ? (
            <ResetButton onClick={actions.onClearAll} size={resetSize} />
          ) : undefined
        }
      />
      <OptionsSeparator />
      <OptionsItemList>
        {rows.map(item => {
          const itemId = category.getItemId(item)
          const isSelected = state.pendingSelection.has(itemId)
          // own label while drilling, breadcrumb in search
          const label = isSearching
            ? category.getItemLabel(item)
            : category.getItemNodeLabel(item)
          const content = category.renderItem
            ? category.renderItem(item, isSelected, label)
            : label

          const isDrillable =
            !isSearching && (childrenByParent.get(itemId)?.length ?? 0) > 0

          if (isDrillable) {
            const drill = () => actions.onDrillInto(itemId)
            return (
              <div
                key={itemId}
                role="option"
                aria-selected={isSelected || undefined}
                className="tw:flex tw:w-full tw:items-center tw:gap-2 tw:rounded-md tw:pl-1.5 tw:pr-2 tw:py-1 tw:text-app-dense tw:transition-colors tw:hover:bg-accent tw:hover:text-accent-foreground"
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => actions.onToggleItem(itemId)}
                  aria-label={category.getItemNodeLabel(item)}
                />
                <div
                  role="button"
                  tabIndex={0}
                  onClick={drill}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      drill()
                    }
                  }}
                  className="tw:flex tw:flex-1 tw:items-center tw:gap-2 tw:min-w-0 tw:cursor-pointer tw:outline-none"
                >
                  <div className="tw:flex-1 tw:min-w-0">{content}</div>
                  <ChevronRight className="tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
                </div>
              </div>
            )
          }

          return (
            <OptionsCheckItem
              key={itemId}
              checked={isSelected}
              onClick={() => actions.onToggleItem(itemId)}
            >
              {content}
            </OptionsCheckItem>
          )
        })}
      </OptionsItemList>
    </>
  )
}
